import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import StudentSection from './StudentSection';
import ExamSection from './ExamSection';
import { useSelector } from 'react-redux';
import { getData } from '../../../../services/apiService';

const HallTicket = () => {
    const [apiResponse, setApiResponse] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);

    const usersData = useSelector((state) => state.user);
    // Get data from usersData
    const rollNo = usersData?.user?.roll_no || null;
    const instId = usersData?.user?.dashboard?.instId  || usersData?.user?.inst_id || null;
    const batch = usersData?.user?.dashboard?.batch || usersData?.user?.atch || null;
    const procYear = usersData?.user?.dashboard?.procYear || usersData?.user?.procYear || null;
    const procSem = usersData?.user?.dashboard?.procSem || usersData?.user?.procSem || null;

    useEffect(() => {
        const fetchExamHallTicket = async () => {
            try {
                setLoading(true);
                setError(false);
                const response = await getData(`/HallTicket/GetHallTicket?instIds=${instId}&procYear=${procYear}&procSem=${procSem}&batch=${batch}&rollno=${rollNo}`);
                setApiResponse(response);
            } catch (error) {
                console.error('Error fetching hall ticket:', error);
                setError(error.message || "An error occurred");
            } finally {
                setLoading(false);
            }
        };
        fetchExamHallTicket();
    }, [rollNo, instId, batch, procYear, procSem]);

    const handlePrint = () => {
        alert("Could not connect Report API")
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0d4f5c]/10 to-[#1a7a8a]/10">
                <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#0d4f5c]"></div>
                    <p className="mt-4 text-lg font-medium text-gray-700">Loading hall ticket...</p>
                </div>
            </div>
        );
    }

    if (error || !apiResponse?.data?.[0]) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0d4f5c]/10 to-[#1a7a8a]/10">
                <div className="bg-white p-8 rounded-xl shadow-lg max-w-md text-center">
                    <div className="text-red-500 mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Hall Ticket</h2>
                    <p className="text-gray-600 mb-6">{error || 'No data available'}</p>
                    <button onClick={() => window.location.reload()} className="px-6 py-2 bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] text-white rounded-md hover:opacity-90 transition">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const currentDate = new Date().toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    // Get data from API response
    const studentData = apiResponse?.data?.[0]?.studentDetails || {};
    const examSchedule = apiResponse?.data?.[0]?.examSchedule || [];
    const ticketNumber = studentData.rollNumber || rollNo || `HT${rollNo}`;

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#0d4f5c]/10 to-[#1a7a8a]/10 py-4 px-4 sm:px-6 lg:px-8 print:py-0 print:px-0">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden border-2 border-[#0d4f5c] print:shadow-none print:border-0 print:rounded-none">
                {/* Header */}
                <div className="relative bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] py-6 px-8 text-white print:bg-[#0d4f5c]">
                    <div className="absolute inset-0 opacity-10 bg-[#0d4f5c]"></div>
                    <div className="relative z-10">
                        <div className="flex justify-between items-start">
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center print:hidden">
                                <img src="https://upload.wikimedia.org/wikipedia/commons/8/83/Emblem_of_Tamil_Nadu.svg" alt="University Logo" className="w-16 h-16 object-contain" />
                            </div>
                            <div className="text-center flex-1">
                                <h1 className="text-3xl font-bold mb-2 tracking-wide">EXAMINATION HALL TICKET</h1>
                                <p className="text-white text-lg">Anna University</p>
                            </div>
                            <div className="w-16 print:hidden"></div>
                        </div>
                        <div className="flex justify-between items-center mt-1 text-sm">
                            <p className="text-white">Academic Year: {studentData.academicYear || 'N/A'}</p>
                            <p className="text-white">Hall Ticket No: <span className="font-semibold">{ticketNumber || 'N/A'}</span></p>
                        </div>
                    </div>
                </div>

                <div className="p-8 print:p-6">
                    <StudentSection studentData={studentData} />
                    <ExamSection examSchedule={examSchedule} />

                    {/* Instructions */}
                    <div className="mb-8 print:mb-6">
                        <h3 className="text-xl font-bold text-[#0d4f5c] border-b-2 border-[#0d4f5c] pb-2 mb-4 print:text-lg print:pb-1 print:mb-3">
                            Important Instructions
                        </h3>
                        <ul className="list-disc pl-5 space-y-2 text-sm print:text-xs">
                            <li className="text-gray-700">Candidates should report to the examination hall 30 minutes before the commencement of the examination.</li>
                            <li className="text-gray-700">Candidates must bring their Hall Ticket and valid ID proof to the examination hall.</li>
                            <li className="text-gray-700">Electronic gadgets including mobile phones, smart watches, and calculators are strictly prohibited inside the examination hall.</li>
                            <li className="text-gray-700">Candidates should write their Roll Number and Hall Ticket Number on the answer booklet.</li>
                            <li className="text-gray-700">No candidate will be permitted to leave the examination hall before the completion of the examination.</li>
                            <li className="text-gray-700">Candidates found indulging in any form of malpractice will be penalized as per the university rules.</li>
                        </ul>
                    </div>

                    {/* Declaration */}
                    <div className="bg-[#0d4f5c]/10 border-l-4 border-[#0d4f5c] p-4 mb-8 print:mb-6">
                        <h4 className="font-bold text-[#0d4f5c] mb-2">Declaration</h4>
                        <p className="text-sm text-gray-700 print:text-xs">
                            I hereby declare that all the information provided is correct. I will abide by all the examination rules and regulations.
                            I understand that any violation may lead to disciplinary action.
                        </p>
                        <div className="flex justify-end mt-12">
                            <div className="text-center">
                                <div className="w-48 h-0.5 bg-[#0d4f5c] mb-1"></div>
                                <p className="text-sm text-gray-600">Student's Signature</p>
                            </div>
                        </div>
                    </div>

                    {/* QR Code */}
                    <div className="print:hidden bg-[#0d4f5c]/10 p-4 rounded-lg flex items-center justify-between border border-[#0d4f5c]">
                        <div>
                            <h4 className="font-bold text-[#0d4f5c] mb-1">Verification QR Code</h4>
                            <p className="text-sm text-gray-600">Scan to verify this hall ticket</p>
                        </div>
                        <QRCodeSVG value={`https://exam-verification.netlify.app/verify/${ticketNumber}`} size={120} level="H" includeMargin={true} />
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-8 py-4 border-t border-gray-200 print:hidden">
                    <div className="flex flex-col sm:flex-row justify-between items-center">
                        <p className="text-sm text-gray-500 mb-2 sm:mb-0">Generated on: {currentDate}</p>
                        <button onClick={handlePrint} className="px-4 py-2 bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] text-white rounded-md hover:opacity-90 transition flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download Hall Ticket
                        </button>
                    </div>
                </div>

                {/* Print Footer */}
                <div className="hidden print:block text-center text-xs text-gray-500 py-2 border-t border-gray-200">
                    <p>This is a computer generated document and does not require signature when printed</p>
                    <p>© {new Date().getFullYear()} Anna University. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default HallTicket;